import { translateAdmin } from '../../utils/adminTranslations';
import AdminText from '../../components/system/AdminText';
import React, { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../../config';
import { fetchData } from '../../utils/api';

import styles from '../../styles/scss/system/Customers.module.scss';

const PAGE_SIZE = 50;

const Customers = ({ setHeaderTitle }) => {
    const [customers, setCustomers] = useState([]);
    const [customersCount, setCustomersCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [emergencyEmail, setEmergencyEmail] = useState('');
    const [emergencyPassword, setEmergencyPassword] = useState('');
    const [emergencyLoading, setEmergencyLoading] = useState(false);
    const [emergencyError, setEmergencyError] = useState('');
    const [emergencyResult, setEmergencyResult] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerBookings, setCustomerBookings] = useState([]);
    const [bookingsLoading, setBookingsLoading] = useState(false);
    const [bookingsError, setBookingsError] = useState('');
    const canSetEmergencyAccess = ['admin', 'booker', 'booker-admin'].includes(localStorage.getItem('asd'));

    useEffect(() => {
        setHeaderTitle('Клиенты');
    }, [setHeaderTitle]);

    const fetchCustomers = useCallback(async (pageToLoad) => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('accessToken');
            const offset = (pageToLoad - 1) * PAGE_SIZE;
            const response = await fetchData(
                `${apiUrl}/api/sys/get-customers?limit=${PAGE_SIZE}&offset=${offset}`,
                'GET',
                token
            );
            if (response.request.status !== 0) {
                throw new Error(response.request.message || 'Не удалось загрузить клиентов');
            }
            setCustomers(response.request.data.customers);
            setCustomersCount(response.request.data.count);
        } catch (loadError) {
            setError(loadError.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCustomers(page);
    }, [fetchCustomers, page]);

    const handleDelete = async (customerId) => {
        if (!window.confirm(translateAdmin('Вы уверены, что хотите удалить этого клиента?'))) return;

        const confirmationWord = window.prompt(translateAdmin('Введите слово УДАЛИТЬ для подтверждения.'));
        if (confirmationWord !== 'УДАЛИТЬ') {
            if (confirmationWord !== null) alert(translateAdmin("Не верно введено 'УДАЛИТЬ'"));
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetchData(
                `${apiUrl}/api/sys/customers/${customerId}/remove`,
                'GET',
                token
            );
            if (response.request.status !== 0) {
                throw new Error(response.request.message || 'Ошибка удаления пользователя');
            }
            alert(translateAdmin(response.request.message));
            if (customers.length === 1 && page > 1) {
                setPage((currentPage) => currentPage - 1);
            } else {
                await fetchCustomers(page);
            }
        } catch (deleteError) {
            alert(deleteError.message);
        }
    };

    const handleEmergencyAccess = async (event) => {
        event.preventDefault();
        if (!window.confirm(translateAdmin(
            'Создать и подтвердить аккаунт для этой почты либо заменить пароль существующего клиента?'
        ))) return;

        setEmergencyLoading(true);
        setEmergencyError('');
        setEmergencyResult(null);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetchData(
                `${apiUrl}/api/sys/customers/emergency-access`,
                'POST',
                token,
                {
                    body: {
                        email: emergencyEmail,
                        password: emergencyPassword,
                    },
                }
            );

            if (response.request.status !== 0) {
                throw new Error(response.request.message || 'Не удалось выдать доступ клиенту');
            }

            setEmergencyResult({
                email: response.request.data.email,
                password: emergencyPassword,
                message: response.request.message,
            });
            setEmergencyEmail('');
            setEmergencyPassword('');
            if (page !== 1) {
                setPage(1);
            } else {
                await fetchCustomers(1);
            }
        } catch (emergencyAccessError) {
            setEmergencyError(emergencyAccessError.message);
        } finally {
            setEmergencyLoading(false);
        }
    };

    const handleShowBookings = async (customer) => {
        setSelectedCustomer(customer);
        setCustomerBookings([]);
        setBookingsError('');
        setBookingsLoading(true);

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetchData(
                `${apiUrl}/api/sys/customers/${customer.id}/bookings`,
                'GET',
                token
            );
            if (response.request.status !== 0) {
                throw new Error(response.request.message || 'Не удалось загрузить брони клиента');
            }
            setCustomerBookings(response.request.data.bookings);
        } catch (loadError) {
            setBookingsError(loadError.message);
        } finally {
            setBookingsLoading(false);
        }
    };

    const formatBookingDate = (dateValue) => {
        if (!dateValue) return '—';
        const date = new Date(dateValue);
        return Number.isNaN(date.getTime()) ? dateValue : date.toLocaleString('ru-RU');
    };

    const pageCount = Math.ceil(customersCount / PAGE_SIZE);
    const firstItem = customersCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const lastItem = Math.min(page * PAGE_SIZE, customersCount);

    return (
        <div className={styles.customersList}>
            <div className={styles.headingRow}>
                <h3 className={styles.customersListHeading}><AdminText>Клиенты:</AdminText> {customersCount}</h3>
                <span className={styles.range}>
                    {loading ? translateAdmin('Загрузка...') : `${firstItem}–${lastItem} ${translateAdmin('из')} ${customersCount}`}
                </span>
            </div>

            {canSetEmergencyAccess && (
                <section className={styles.emergencyPanel}>
                    <h4><AdminText>Экстренный доступ клиента</AdminText></h4>
                    <p>
                        <AdminText>Создаёт подтверждённый аккаунт без кода либо заменяет пароль существующего. Передайте клиенту email и заданный пароль безопасным способом.</AdminText>
                    </p>
                    <form className={styles.emergencyForm} onSubmit={handleEmergencyAccess}>
                        <label>
                            <AdminText>Email клиента</AdminText>
                            <input
                                type="email"
                                autoComplete="off"
                                required
                                value={emergencyEmail}
                                onChange={(event) => setEmergencyEmail(event.target.value)}
                            />
                        </label>
                        <label>
                            <AdminText>Пароль (минимум 5 символов)</AdminText>
                            <input
                                type="password"
                                autoComplete="new-password"
                                minLength={5}
                                required
                                value={emergencyPassword}
                                onChange={(event) => setEmergencyPassword(event.target.value)}
                            />
                        </label>
                        <button type="submit" disabled={emergencyLoading}>
                            {emergencyLoading ? translateAdmin('Обработка...') : translateAdmin('Создать аккаунт / установить пароль')}
                        </button>
                    </form>
                    {emergencyError && (
                        <p className={styles.error} role="alert">{emergencyError}</p>
                    )}
                    {emergencyResult && (
                        <div className={styles.emergencyResult} role="status">
                            <strong>{emergencyResult.message}</strong>
                            <span>Email: {emergencyResult.email}</span>
                            <span><AdminText>Пароль:</AdminText> {emergencyResult.password}</span>
                        </div>
                    )}
                </section>
            )}

            {error && <p className={styles.error} role="alert">{error}</p>}

            <div className={styles.tableWrapper} aria-busy={loading}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>№</th>
                            <th>Email</th>
                            <th><AdminText>Номер телефона</AdminText></th>
                            <th><AdminText>Зарегистрировался</AdminText></th>
                            <th><AdminText>Брони</AdminText></th>
                            <th><AdminText>Действия</AdminText></th>
                        </tr>
                    </thead>
                    <tbody>
                        {customers.map((customer, index) => (
                            <tr key={customer.id}>
                                <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                                <td>{customer.email}</td>
                                <td>{customer.phone_number || '-'}</td>
                                <td>{customer.created_at}</td>
                                <td>{customer.booking_count}</td>
                                <td>
                                    <button
                                        type="button"
                                        className={styles.buttonView}
                                        onClick={() => handleShowBookings(customer)}
                                        disabled={loading}
                                    >
                                        <AdminText>Просмотр броней</AdminText>
                                    </button>
                                    <button
                                        className={styles.buttonDelete}
                                        onClick={() => handleDelete(customer.id)}
                                        disabled={loading}
                                    >
                                        <AdminText>Удалить</AdminText>
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!loading && customers.length === 0 && !error && (
                            <tr>
                                <td className={styles.emptyState} colSpan="6"><AdminText>Клиенты не найдены.</AdminText></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {pageCount > 1 && (
                <div className={styles.pagination}>
                    <button
                        type="button"
                        onClick={() => setPage((currentPage) => currentPage - 1)}
                        disabled={loading || page <= 1}
                    >
                        <AdminText>Назад</AdminText>
                    </button>
                    <span>Страница {page} из {pageCount}</span>
                    <button
                        type="button"
                        onClick={() => setPage((currentPage) => currentPage + 1)}
                        disabled={loading || page >= pageCount}
                    >
                        <AdminText>Далее</AdminText>
                    </button>
                </div>
            )}

            {selectedCustomer && (
                <div
                    className={styles.bookingsOverlay}
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setSelectedCustomer(null);
                    }}
                >
                    <section
                        className={styles.bookingsDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="customer-bookings-title"
                    >
                        <header className={styles.bookingsHeader}>
                            <div>
                                <span className={styles.bookingsEyebrow}><AdminText>ИСТОРИЯ БРОНИРОВАНИЙ</AdminText></span>
                                <h3 id="customer-bookings-title"><AdminText>Брони клиента</AdminText></h3>
                                <p>{selectedCustomer.email}</p>
                            </div>
                            <button
                                type="button"
                                className={styles.closeDialog}
                                onClick={() => setSelectedCustomer(null)}
                                aria-label={translateAdmin("Закрыть")}
                            >
                                ×
                            </button>
                        </header>

                        <div className={styles.bookingCards}>
                            {bookingsLoading && <p className={styles.bookingMessage}><AdminText>Загрузка броней...</AdminText></p>}
                            {bookingsError && <p className={styles.error} role="alert">{bookingsError}</p>}
                            {!bookingsLoading && !bookingsError && customerBookings.length === 0 && (
                                <p className={styles.bookingMessage}><AdminText>У клиента пока нет броней.</AdminText></p>
                            )}
                            {!bookingsLoading && customerBookings.map((booking) => (
                                <article className={styles.bookingCard} key={booking.id}>
                                    <div className={styles.bookingCardHeader}>
                                        <div>
                                            <strong>Бронь {booking.book_number || `#${booking.id}`}</strong>
                                            <span><AdminText>Номер комнаты:</AdminText> {booking.room_number || translateAdmin('не назначен')}</span>
                                        </div>
                                        <span className={styles.bookingStatus}>
                                            {booking.checkedin
                                                ? (booking.checkedout ? translateAdmin('Выселен') : translateAdmin('Заселён'))
                                                : (booking.status === 0 ? translateAdmin('Оплачена') : translateAdmin('Ожидает оплаты'))}
                                        </span>
                                    </div>
                                    <div className={styles.bookingDates}>
                                        <span><AdminText>Заезд:</AdminText> {formatBookingDate(booking.available_from)}</span>
                                        <span><AdminText>Выезд:</AdminText> {formatBookingDate(booking.available_to)}</span>
                                        <span><AdminText>Заявка создана:</AdminText> {formatBookingDate(booking.created_at)}</span>
                                        <span><AdminText>Оплачено:</AdminText> {formatBookingDate(booking.payment_at)}</span>
                                    </div>
                                    <div className={styles.guestsBlock}>
                                        <strong><AdminText>Гости</AdminText> ({booking.guests.length})</strong>
                                        {booking.guests.length > 0 ? (
                                            <ul>
                                                {booking.guests.map((guest, guestIndex) => (
                                                    <li key={`${booking.id}-${guestIndex}`}>
                                                        <span>{guest.full_name || translateAdmin('Имя не указано')}</span>
                                                        {guest.passport_serial_number && (
                                                            <small><AdminText>Паспорт:</AdminText> {guest.passport_serial_number}</small>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p><AdminText>Гости не указаны.</AdminText></p>
                                        )}
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default Customers;
